/**
 * AxisInfo → normalized axis plan.
 *
 * Flattens the contract AxisInfo into the concrete values a SciChart NumericAxis
 * needs: side, autoRange, visible range, log scale, label precision (derived
 * from the number-format string), and resolved grid styling.
 */

import { resolveColorRef, type AxisInfo, type ChartGroup, type Theme } from "@qufolio/model";

/** The group-level gridline choice. `ChartGroup["gridLines"]`, minus undefined. */
export type GridLineStyle = NonNullable<ChartGroup["gridLines"]>;

export const DEFAULT_GRID_LINES: GridLineStyle = "solid";

/**
 * Dash patterns for the named styles, in device pixels: `[on, off, …]`.
 *
 * Dotted is `[1, 3]` rather than `[1, 1]` — at one-on-one the dots merge into a
 * grey line at typical gridline thickness, which is a worse solid line rather
 * than a dotted one. Solid and none carry no pattern: solid because that is the
 * absence of one, none because the line is not drawn at all.
 */
const GRID_DASH: Record<GridLineStyle, number[] | undefined> = {
  solid: undefined,
  dashed: [4, 4],
  dotted: [1, 3],
  none: undefined,
};

/** The dash array a named style draws with, if any. */
export function gridDashFor(style: GridLineStyle = DEFAULT_GRID_LINES): number[] | undefined {
  return GRID_DASH[style];
}

/**
 * Roughly how far apart major gridlines should sit, in device pixels.
 *
 * A tick label is about eleven pixels tall; at this spacing the labels have air
 * around them and the lines read as a scale. Much tighter and the axis becomes
 * hatching, which is the complaint this exists to answer.
 */
export const MAJOR_GRID_SPACING_PX = 46;

/** Below two there is nothing to read a value against, and the labels float. */
export const MIN_MAJOR_GRID_LINES = 2;

/** SciChart's own `maxAutoTicks` default, and the ceiling for a tall pane. */
export const MAX_MAJOR_GRID_LINES = 10;

/**
 * How many major gridlines a pane this tall should draw.
 *
 * The count has to **fall with the pane's height**: a fixed ten is fine over a
 * 500px price pane and is noise in the 80px an oscillator gets after a divider
 * drag. So it is derived from the rendered height rather than stored — which is
 * also why the caller measures and this only decides, and why pane height stays
 * out of the render plan's structural key (measuring is a repaint; a key change
 * is a WebGL teardown and a refetch).
 *
 * `grid.minorsPerMajor` was the other candidate and is the wrong lever: it
 * subdivides the majors that already exist, so raising it in a short pane adds
 * lines to a pane that has too many. The major count is the one that has to move.
 *
 * An unmeasured container (zero or NaN — laid out but not yet sized) yields the
 * ceiling rather than the floor, so a chart that renders before its first
 * measurement looks exactly as SciChart's default does instead of pinning itself
 * to two gridlines and staying there.
 */
export function maxMajorGridLines(paneHeightPx: number): number {
  if (!Number.isFinite(paneHeightPx) || paneHeightPx <= 0) return MAX_MAJOR_GRID_LINES;
  const fitted = Math.round(paneHeightPx / MAJOR_GRID_SPACING_PX);
  return Math.min(MAX_MAJOR_GRID_LINES, Math.max(MIN_MAJOR_GRID_LINES, fitted));
}

/**
 * A bar's timestamp as an axis label — **UTC**, which SciChart's own default is not.
 *
 * `DiscontinuousDateAxis` defaults to a `SmartDateLabelProvider`, whose
 * `labelFormat` and `cursorLabelFormat` are both `ENumericFormat.Date_DDMMYYYY`. That format
 * routes to `formatUnixDateToHumanString`, and that one function omits the
 * `timeZone: "utc"` its siblings in the same file all pass:
 *
 * ```js
 * // scichart/utils/date.js
 * new Date(unixTimestamp * 1000).toLocaleDateString(locale, { month, year, day })
 * ```
 *
 * A daily bar is UTC midnight, so west of Greenwich every label lands on the
 * previous day — `2026-08-04T00:00:00Z` reads as `8/3/2026` at UTC-7. Not a
 * rounding error and not confined to one bar: the whole axis and the crosshair
 * are shifted, uniformly, while the data behind them is right. It is the same
 * off-by-one `chart-window.ts` and `ChartDataInspector` already carry notes
 * about, arriving here from inside the dependency rather than from our code.
 *
 * Same shape as the format it replaces (`M/D/YYYY`, `en-US`) so nothing moves
 * but the day. Date-only, again matching it — every frequency the chart serves
 * is midnight-stamped, and a time nobody can read costs axis width.
 */
export function formatBarDateUtc(seconds: number): string {
  if (!Number.isFinite(seconds)) return "";
  const at = new Date(seconds * 1000);
  // **Finite is not the same as valid.** `Number.isFinite` passes 1e13, and
  // `new Date(1e16)` is beyond the ±8.64e15 ms a Date can hold — so it is an
  // *invalid* Date whose `toLocaleDateString` returns the literal string
  // "Invalid Date". That is what appeared along the axis past the last bar: not
  // an error, not a blank, but a label reading "Invalid Date" on every tick.
  //
  // The axis asks for a label wherever it puts a tick, including outside the
  // data, so this has to answer "nothing" rather than "something wrong".
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

/**
 * How the time axis writes a bar's timestamp.
 *
 * `date` is every interval built from the daily file — midnight-stamped, UTC,
 * `formatBarDateUtc`. `time` is intraday, and it is read **in the exchange's
 * own zone**: a 09:30 bar is 09:30 to a New York trader wherever the reader
 * sits, and a label in the reader's zone would put the open at 06:30 for
 * California. `seconds` adds `:SS`, for a seconds chart where every label in a
 * minute would otherwise read the same.
 */
export type TimeAxisLabels =
  | { kind: "date" }
  | { kind: "time"; timeZone: string; seconds: boolean };

export const DATE_LABELS: TimeAxisLabels = { kind: "date" };

/** The axis tick label and the crosshair readout, for one `TimeAxisLabels`. */
export function timeAxisFormatters(labels: TimeAxisLabels): {
  axis: (seconds: number) => string;
  cursor: (seconds: number) => string;
} {
  if (labels.kind === "date") return { axis: formatBarDateUtc, cursor: formatBarDateUtc };
  const clock: Intl.DateTimeFormatOptions = {
    timeZone: labels.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    ...(labels.seconds ? { second: "2-digit" } : {}),
    hourCycle: "h23",
  };
  // Built once per axis rather than per label: the axis asks for a label at
  // every tick on every frame of a pan.
  const tick = new Intl.DateTimeFormat("en-US", { ...clock, month: "numeric", day: "numeric" });
  const cursor = new Intl.DateTimeFormat("en-US", {
    ...clock,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
  const guard = (format: Intl.DateTimeFormat) => (seconds: number) => {
    if (!Number.isFinite(seconds)) return "";
    const at = new Date(seconds * 1000);
    // `formatBarDateUtc`'s reason: a tick past the data can be an invalid Date.
    return Number.isNaN(at.getTime()) ? "" : format.format(at);
  };
  return { axis: guard(tick), cursor: guard(cursor) };
}

export interface AxisGridPlan {
  /** False for `"none"` — the line is suppressed, not drawn transparent. */
  draw: boolean;
  majorColor?: string;
  majorThickness?: number;
  majorDash?: number[];
  minorsPerMajor: number;
}

export interface AxisPlan {
  id: string;
  side: "left" | "right";
  autoRange: "always" | "once" | "never";
  visibleRange?: { min: number; max: number };
  /** Whether the axis draws its own furniture. Hidden takes no width. */
  visible: boolean;
  /**
   * The slice of the pane this axis draws into, 0 at the bottom.
   *
   * Absent means the whole pane. Applied by the Y fit rather than by the axis —
   * a band is a statement about where a *fitted* range lands, so there is
   * nothing for the renderer to honour until something has fitted it.
   */
  band?: { from: number; to: number };
  logarithmic: boolean;
  /** `AxisInfo.scale: "signedLog"` — see `plan/signed-log.ts`. */
  signedLog: boolean;
  labelFormat?: string;
  labelPrecision?: number;
  grid: AxisGridPlan;
}

/** Decimal precision implied by a number-format string ("0.00" → 2, "0,0" → 0). */
export function precisionFromFormat(format?: string): number | undefined {
  if (!format) return undefined;
  const dot = format.indexOf(".");
  if (dot === -1) return 0;
  let n = 0;
  for (let i = dot + 1; i < format.length; i++) {
    const ch = format[i];
    if (ch === "0" || ch === "#") n++;
    else break;
  }
  return n;
}

/**
 * @param groupStyle The chart group's `gridLines`. A per-axis `grid.majorStyle.dash`
 * overrides it — the group setting is a default, not a lock, so one axis can stay
 * solid while the rest go dotted.
 */
export function resolveAxisPlan(
  axis: AxisInfo,
  theme?: Theme,
  groupStyle: GridLineStyle = DEFAULT_GRID_LINES,
): AxisPlan {
  const grid = axis.grid ?? {};
  const major = grid.majorStyle ?? {};

  // Both ends or neither: a band with one edge is a slice with no height, and
  // silently treating the missing edge as 0 or 1 would put the series somewhere
  // nobody asked for.
  const b = axis.band;
  const band =
    b && typeof b.from === "number" && typeof b.to === "number" && b.to > b.from
      ? { from: b.from, to: b.to }
      : undefined;

  const vr = axis.visibleRange;
  const visibleRange =
    vr && typeof vr.min === "number" && typeof vr.max === "number"
      ? { min: vr.min, max: vr.max }
      : undefined;

  return {
    id: axis.id,
    side: axis.side,
    autoRange: axis.autoRange ?? "always",
    visibleRange,
    visible: axis.visible ?? true,
    ...(band ? { band } : {}),
    logarithmic: axis.scale === "logarithmic",
    signedLog: axis.scale === "signedLog",
    labelFormat: axis.format,
    labelPrecision: precisionFromFormat(axis.format),
    grid: {
      draw: groupStyle !== "none",
      majorColor: resolveColorRef(major.color ?? axis.gridColor, theme),
      majorThickness: major.thickness,
      majorDash: major.dash ?? gridDashFor(groupStyle),
      minorsPerMajor: grid.minorsPerMajor ?? 2,
    },
  };
}